class QualucaSlimProgramSelector extends HTMLElement {
  connectedCallback() {
    document.documentElement.classList.add('qspdp-page');
    this.priceLabel = this.dataset.priceLabel || '';
    this.abortController = new AbortController();
    this.handledCartAddEvents = new WeakSet();
    this.bindCartDrawerOpen();
    this.bindAccordions();
    this.enhanceTimers = [0, 500, 1500, 3000].map((delay) =>
      window.setTimeout(() => {
        this.enhanceAddToCartButtons();
        this.enhanceReviewAggregate();
        this.setupStickyFallback();
      }, delay),
    );
  }

  disconnectedCallback() {
    this.enhanceTimers?.forEach((timer) => window.clearTimeout(timer));
    this.abortController?.abort();
  }

  bindCartDrawerOpen() {
    document.addEventListener(
      'cart:update',
      (event) => {
        const data = event.detail?.data;
        if (
          !data ||
          data.source !== 'product-form-component' ||
          String(data.productId || '') !== String(this.dataset.productId || '')
        ) {
          return;
        }

        if (
          data.didError === true ||
          this.handledCartAddEvents.has(event) ||
          !document.querySelector('[data-ebpcart]')
        ) {
          return;
        }

        this.handledCartAddEvents.add(event);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      },
      { signal: this.abortController.signal },
    );
  }

  setupStickyFallback() {
    if (this.stickyFallbackReady) return;

    const stickyBar = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__bar');
    const mainButton = this.querySelector('.qspdp__buy .add-to-cart-button');
    if (!stickyBar || !mainButton) return;

    this.stickyFallbackReady = true;
    const stickyButton = stickyBar.querySelector('.sticky-add-to-cart__button');
    if (stickyButton) {
      stickyButton.setAttribute('aria-label', `Adaugă în coș – ${this.priceLabel}`);
      stickyButton.dataset.qspdpBound = 'true';
    }

    const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
    const syncSticky = () => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;

      const buttonRect = mainButton.getBoundingClientRect();
      const footerIsNear = footer ? footer.getBoundingClientRect().top <= window.innerHeight + 120 : false;
      stickyBar.dataset.stuck = String(buttonRect.bottom < 0 && !footerIsNear);
    };

    window.addEventListener('scroll', syncSticky, {
      passive: true,
      signal: this.abortController.signal,
    });
    window.addEventListener('resize', syncSticky, {
      passive: true,
      signal: this.abortController.signal,
    });
    syncSticky();
  }

  bindAccordions() {
    this.querySelectorAll('.qspdp__accordion').forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary || summary.dataset.qspdpBound === 'true') return;

      summary.dataset.qspdpBound = 'true';
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        details.toggleAttribute('open');
      });
      summary.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        details.toggleAttribute('open');
      });
    });
  }

  enhanceReviewAggregate() {
    const expectedCount = Number(this.dataset.reviewSourceCount) || 0;
    const widgets = [...this.querySelectorAll('.qspdp__review-sources .jdgm-preview-badge')];
    const seenProductIds = new Set();
    const reviews = [];

    for (const widget of widgets) {
      const productId = widget.dataset.id;
      if (!productId || seenProductIds.has(productId)) continue;

      const badge = widget.querySelector('.jdgm-prev-badge');
      if (!badge?.hasAttribute('data-number-of-reviews') || !badge.hasAttribute('data-average-rating')) {
        return;
      }

      const count = Number(badge.dataset.numberOfReviews);
      const rating = Number(badge.dataset.averageRating);
      if (!Number.isFinite(count) || !Number.isFinite(rating) || count < 0 || rating < 0) return;

      seenProductIds.add(productId);
      reviews.push({ count, rating });
    }

    if (!expectedCount || reviews.length !== expectedCount) return;

    const total = reviews.reduce((sum, review) => sum + review.count, 0);
    if (!total) return;

    const weightedTotal = reviews.reduce((sum, review) => sum + review.rating * review.count, 0);
    const rating = Math.round((weightedTotal / total) * 100) / 100;
    const ratingLabel = rating.toLocaleString('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const ratingValue = this.querySelector('[data-qspdp-rating]');
    const reviewCount = this.querySelector('[data-qspdp-review-count]');
    const ratingLink = this.querySelector('.qspdp__rating-link');
    const stars = this.querySelector('.qspdp__stars');

    if (ratingValue) ratingValue.textContent = `${ratingLabel}/5`;
    if (reviewCount) reviewCount.textContent = String(total);
    if (ratingLink) {
      ratingLink.setAttribute(
        'aria-label',
        `${ratingLabel} din 5, din ${total} recenzii publicate pentru produsele din colecțiile selectate`,
      );
    }
    if (stars) stars.style.setProperty('--rating-width', `${Math.min(100, (rating / 5) * 100)}%`);
  }

  enhanceAddToCartButtons() {
    if (!this.priceLabel) return;

    const selectors = [
      '.qspdp__buy .add-to-cart-text__content > span > span',
      'sticky-add-to-cart .add-to-cart-text__content > span > span',
      '.sticky-add-to-cart .add-to-cart-text__content > span > span',
    ];

    document.querySelectorAll(selectors.join(',')).forEach((label) => {
      if (label.querySelector('.qspdp__atc-price')) return;

      const price = document.createElement('span');
      price.className = 'qspdp__atc-price';
      price.textContent = ` • ${this.priceLabel}`;
      price.setAttribute('aria-hidden', 'true');
      label.append(price);

      const button = label.closest('button');
      if (button) {
        button.setAttribute('aria-label', `Adaugă în coș – ${this.priceLabel}`);
      }
    });
  }
}

if (!customElements.get('qualuca-slim-program-selector')) {
  customElements.define('qualuca-slim-program-selector', QualucaSlimProgramSelector);
}
